(function () {
    const VIDEO_SELECTOR = 'video-media, video-with-facade';
    const NO_CONSENT_CLASS = 'no-consent';
    // Fired on the initial resolution and on every later change, so video
    // elements can gate loading without caring which happened first: this
    // script running, or their own module.
    const CONSENT_EVENT = 'vimeo-consent:change';
    // Set by snippets/vimeo-consent-early.liquid before first paint; see the CSS
    // it drives in components-custom.css.
    const PENDING_CLASS = 'video-consent-pending';

    // null until the customer privacy API answers. Elements are left untouched
    // while unknown — PENDING_CLASS keeps facades hidden over that window, so
    // neither outcome repaints over the other.
    let preferencesAllowed = null;
    let pendingApply = false;

    function applyToAll() {
        if (preferencesAllowed === null) return;

        document.querySelectorAll(VIDEO_SELECTOR).forEach(el => {
            el.classList.toggle(NO_CONSENT_CLASS, !preferencesAllowed);
        });
    }

    // Coalesce to one pass per frame: the observer below can fire many times in
    // a row while a section renders its markup.
    function scheduleApply() {
        if (pendingApply || preferencesAllowed === null) return;

        pendingApply = true;
        requestAnimationFrame(() => {
            pendingApply = false;
            applyToAll();
        });
    }

    function setPreferencesAllowed(value) {
        preferencesAllowed = !!value;
        // Classes first, then reveal, so facades are never shown against a
        // state that hasn't been applied yet.
        applyToAll();
        document.documentElement.classList.remove(PENDING_CLASS);
        document.dispatchEvent(new CustomEvent(CONSENT_EVENT, {
            detail: { preferencesAllowed: preferencesAllowed }
        }));
    }

    function readPreferencesAllowed() {
        const api = window.Shopify && window.Shopify.customerPrivacy;
        if (!api || typeof api.preferencesProcessingAllowed !== 'function') return null;

        try {
            return api.preferencesProcessingAllowed();
        } catch (e) {
            return null;
        }
    }

    // Videos stay gated until this resolves, so if the API can't be reached the
    // state resolves to denied rather than being left unknown forever: visitors
    // get the fallback message instead of an empty box, and nothing third-party
    // loads without a positive answer.
    function unavailable(reason) {
        console.warn(`⚠️ Customer privacy API unavailable (${reason}); Vimeo embeds gated.`);
        setPreferencesAllowed(false);
    }

    function whenPrivacyApiReady(callback) {
        if (readPreferencesAllowed() !== null) return callback();

        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
                if (readPreferencesAllowed() !== null) {
                    callback();
                } else {
                    unavailable(error ? 'loadFeatures error' : 'no API after load');
                }
            });
            return;
        }

        // Shopify global not there yet (or an app supplies the API): poll for it.
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            if (readPreferencesAllowed() !== null) {
                clearInterval(interval);
                callback();
            } else if (attempts === 200) {
                clearInterval(interval);
                unavailable('timed out');
            }
        }, 50);
    }

    whenPrivacyApiReady(() => setPreferencesAllowed(readPreferencesAllowed()));

    document.addEventListener('visitorConsentCollected', e => {
        setPreferencesAllowed(e.detail.preferencesAllowed);
    });

    // Sections that build their markup client-side (video-grid) and is-land,
    // which moves elements in and out of the document, both add matching
    // elements long after this script runs — a one-time NodeList would miss them.
    new MutationObserver(mutations => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) continue;
                if (node.matches(VIDEO_SELECTOR) || node.querySelector(VIDEO_SELECTOR)) {
                    scheduleApply();
                    return;
                }
            }
        }
    }).observe(document.body, { childList: true, subtree: true });

    // Read by renderers that stamp the class into markup as they build it.
    window.vimeoConsent = {
        get preferencesAllowed() {
            return preferencesAllowed === true;
        },
        get isKnown() {
            return preferencesAllowed !== null;
        },
        applyToAll
    };
})();
